import DataGrid from '../../../../table/DataGrid'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import CustomAvatar from '../../../../avatar/Avatar'
import DialogPreviewAboutMe from '../../components/DialogPreviewAboutMe/DialogPreviewAboutMe'
import useMySurveys from '../../../../../hooks/data-entry/useMySurver'
import FilterCompletionButtom from './FilterCompletion/Button'
import { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { Button, Chip, Box } from '@mui/material'
import { useParams } from 'react-router-dom'
import {
  SURVEY_TYPE_ABOUT_CLASSES,
  SURVEY_TYPE_ABOUT_TEACHER,
  SURVEY_TYPE_ABOUT_USERS
} from '../../constants/typeSurvey'
import { exportToCSV } from '../../../../../utils/file'
import { getSurvey } from '../../../../../services/data-entry/surveys.service'

const TableAbout = ({ type = '', filterx, onFilter, disabled, dataYears = [] }) => {
  const { surveyId } = useParams()
  const { onShareLink } = useMySurveys()
  const { enqueueSnackbar } = useSnackbar()
  const template = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.template ?? {})
  const results = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.surveyResults ?? [])
  const data = useSelector((state) => state?.dataEntrySurveyPreview?.surveyPreview ?? {})

  const tempList = useSelector((state) =>
    type === SURVEY_TYPE_ABOUT_CLASSES
      ? (state.dataEntrySurveyPreview?.surveyPreview?.data?.data ?? [])
      : (state.dataEntrySurveyPreview?.surveyPreview?.data?.data ?? [])
  )

  const columnsTableClass = useSelector((state) => state?.datatable?.surveyClassList?.columnsTable ?? [])
  const columnsTableUsers = useSelector((state) => state?.datatable?.surveyUsersList?.columnsTable ?? [])
  const loading = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.loading ?? false)
  const skip = useSelector((state) => state?.datatable?.surveyPreviewList?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyPreviewList?.rowsPerPage ?? 100)
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {})
  const filter = useSelector((state) => state?.filter?.surveyPreviewList?.filter || {})
  const [list, setList] = useState(tempList)
  const [modal, setModal] = useState(null)
  const [isExportingStudents, setIsExportingStudents] = useState(false)

  const onPreview = (row) => {
    setModal({
      open: true,
      data: row
    })
  }

  const getFilteredItems = (items = [], filter) => {
    let newList = items
    if (filter?.code) {
      newList = newList.filter((item) => item.code?.toLowerCase().includes(filter.code.toLowerCase()))
    }
    if (filter?.name) {
      newList = newList.filter(
        (item) =>
          item?.firstName?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item?.preferredName?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item?.lastName?.toLowerCase().includes(filter.name.toLowerCase())
      )
    }
    if (filter?.status) {
      newList = newList.filter((item) => item.status === filter.status)
    }
    if (filter?.yearLevel?.length > 0) {
      const filterNames = filter?.yearLevel?.map((yl) => yl?.name) ?? []
      const filterIds = filter?.yearLevelId ?? []
      newList = newList.filter((item) =>
        item?.yearLevel?.some((yl) =>
          typeof yl === 'string' ? filterIds.includes(yl) : filterNames.includes(yl?.name)
        )
      )
    }

    return newList
  }

  const filterData = (list = [], skipValue = 0, rowsPerPageValue = 50, filter) => {
    let newList = getFilteredItems(list, filter)

    if (
      !Array.isArray(newList) ||
      !Number.isInteger(skipValue) ||
      !Number.isInteger(rowsPerPageValue) ||
      skipValue < 0 ||
      rowsPerPageValue <= 0
    ) {
      return newList
    }

    const startIndex = skipValue * rowsPerPageValue
    const endIndex = startIndex + rowsPerPageValue

    return newList.slice(startIndex, Math.min(endIndex, newList.length))
  }

  const handleExportStudents = async () => {
    try {
      setIsExportingStudents(true)

      const response = await getSurvey(surveyId, true)
      const fullData = response?.data?.data?.data || []

      // Get the full filtered list without pagination
      const filteredItems = getFilteredItems(fullData, filter)

      if (filteredItems.length === 0) {
        enqueueSnackbar('No results found to export', { variant: 'warning' })
        return
      }

      // Map data for export
      const exportData = []

      for (const item of filteredItems) {
        const { dataResults = [] } = item
        const submittedResults = dataResults.filter((res) => !!res?.isSubmitted)

        submittedResults.forEach((result) => {
          const yearLevel = result?.userData?.data?.yearLevel || ''
          const foundYear = dataYears?.find((yl) => yl?._id === yearLevel)

          exportData.push({
            studentId: result?.userData?.code || '',
            firstName: result?.userData?.firstName || '',
            lastName: result?.userData?.lastName || '',
            email: result?.userData?.email || '',
            yearLevel: foundYear?.name || '',
            statusSurvey: 'Completed',
            role: result?.userData?.roles?.map((role) => role).join(', '),
            from: result?.userData?.data?.form || '',
            house: result?.userData?.data?.house || '',
            flag: result?.userData?.data?.flag?.join(', ') || '',
            targetCode: item?.code,
            targetUserName: item?.name,
            targetUserEmail: item?.email,
            targetUserRole: item?.roles?.map((role) => role?.name).join(', ')
          })
        })

        // MF-510: also include the students who have NOT completed the survey as
        // "Pending" (class roster minus the students who submitted a response).
        const completedCodes = new Set(submittedResults.map((res) => res?.userData?.code).filter(Boolean))
        const pendingStudents = (item?.students || []).filter(
          (student) => student?.code && !completedCodes.has(student.code)
        )

        pendingStudents.forEach((student) => {
          const yearLevel = student?.data?.yearLevel || student?.yearlevel || ''
          const foundYear = dataYears?.find((yl) => yl?._id === yearLevel)

          exportData.push({
            studentId: student?.code || '',
            firstName: student?.firstName || '',
            lastName: student?.lastName || '',
            email: student?.email || '',
            yearLevel: foundYear?.name || '',
            statusSurvey: 'Pending',
            role: Array.isArray(student?.roles)
              ? student.roles.map((role) => role?.name || role).join(', ')
              : '',
            from: student?.data?.form || '',
            house: student?.data?.house || '',
            flag: student?.data?.flag?.join(', ') || '',
            targetCode: item?.code,
            targetUserName: item?.name,
            targetUserEmail: item?.email,
            targetUserRole: item?.roles?.map((role) => role?.name).join(', ')
          })
        })
      }

      // Set headers
      let headers = [
        { label: 'User ID', key: 'studentId' },
        { label: 'First Name', key: 'firstName' },
        { label: 'Last Name', key: 'lastName' },
        { label: 'Email', key: 'email' },
        { label: 'Year Level', key: 'yearLevel' },
        { label: 'Survey Status', key: 'statusSurvey' },
        { label: 'Role', key: 'role' },
        { label: 'Form', key: 'from' },
        { label: 'House', key: 'house' },
        { label: 'Flag', key: 'flag' }
      ]

      if (type === SURVEY_TYPE_ABOUT_CLASSES) {
        headers = [...headers, { label: 'Target Class', key: 'targetCode' }]
      }

      if (SURVEY_TYPE_ABOUT_USERS.includes(type)) {
        headers = [
          ...headers,
          { label: 'Target User ID', key: 'targetCode' },
          { label: 'Target User Name', key: 'targetUserName' },
          { label: 'Target User Email', key: 'targetUserEmail' },
          { label: 'Target User Role', key: 'targetUserRole' }
        ]
      }

      // // Generate filename
      const surveyName = data?.name || 'survey'
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `${surveyName} - Users Report ${timestamp}`

      exportToCSV(exportData, headers, filename)
    } catch (error) {
      enqueueSnackbar('Error exporting results', { variant: 'error' })
    } finally {
      setIsExportingStudents(false)
    }
  }

  useEffect(() => {
    setList(filterData(tempList, skip, rowsPerPage, filter))
  }, [tempList, skip, rowsPerPage, filter])

  const columnsTable = useMemo(() => {
    let cols = []

    if (type === SURVEY_TYPE_ABOUT_CLASSES) {
      cols = columnsTableClass.map((col) => ({
        ...col,
        hide: false,
        show: true
      }))
    }
    if (SURVEY_TYPE_ABOUT_USERS.includes(type)) {
      cols = columnsTableUsers.map((col) => ({
        ...col,
        hide: false,
        show: true
      }))
    }

    return cols
  }, [type])

  return (
    <>
      <DataGrid
        actionsSetting={
          <Box className='flex items-center gap-2'>
            <FilterCompletionButtom
              disabled={disabled}
              filter={filter}
              onFilter={onFilter}
              dataYears={dataYears}
              type={type}
            />

            {permissions['list'] && tempList.length > 0 && (
              <Box className='flex items-center gap-2' sx={{ minWidth: '180px' }}>
                <Button
                  variant='outlined'
                  size='small'
                  disabled={disabled || loading || isExportingStudents}
                  onClick={handleExportStudents}
                  startIcon={
                    <FontAwesomeIcon
                      icon={['fal', isExportingStudents ? 'spinner' : 'download']}
                      spin={isExportingStudents}
                    />
                  }
                  className='export-button'
                  title='Export Users List'
                >
                  {isExportingStudents ? 'Exporting...' : 'Export Summary'}
                </Button>
              </Box>
            )}
          </Box>
        }
        labelNoRows={`You don't have any ${type.replace('about-', '')} yet`}
        id={`survey-preview-table-${type}`}
        loading={loading}
        rows={list?.map((row, index) => {
          const yearLevel = dataYears?.filter((y) => row.yearLevel?.includes(y._id))
          const levels = typeof row?.yearLevel?.[0] === 'string' ? yearLevel : row.yearLevel
          // MF-548: about teachers also knows its expected students, so show "answered/expected".
          const hasRoster = type === SURVEY_TYPE_ABOUT_CLASSES || type === SURVEY_TYPE_ABOUT_TEACHER
          const text = hasRoster
            ? `student${row?.students?.length === 1 ? '' : 's'}`
            : `user${row?.results === 1 ? '' : 's'}`
          const completed = hasRoster
            ? `${row?.results || 0}/${row?.students?.length || 0} ${text}`
            : `${row?.results || 0} ${text}`
          return {
            ...row,
            index: index + 1 + rowsPerPage * skip,
            avatar: <CustomAvatar size='small' alt={row?.code} src={`photos/${row?.photo}`} />,
            code: row.code ?? '',
            name: `${row?.preferredName || row?.firstName} ${row?.lastName}`,
            roles: (
              <div className='flex flex-wrap gap-1'>
                {row?.roles?.map((role) => (
                  <Chip key={`user-${row._id}-role-${role._id}`} size='small' label={role.name} />
                ))}
              </div>
            ),
            rolesSort: row?.roles?.map((role) => role?.name).join(', '),
            teachers: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.teachers?.map((teacher) => (
                  <Chip
                    key={`user-${row._id}-teacher-${teacher?._id}`}
                    size='small'
                    label={`${teacher?.preferredName || teacher?.firstName} ${teacher?.lastName}`}
                    variant='outlined'
                  />
                ))}
              </div>
            ),
            yearLevelUser: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.yearLevel?.map((yearLevel) => (
                  <Chip
                    key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                    size='small'
                    label={yearLevel.name}
                    variant='outlined'
                  />
                ))}
              </div>
            ),
            yearLevelUserSort: row?.yearLevel?.map((yearLevel) => yearLevel?.name).join(', '),
            yearLevel: (
              <div className='flex flex-wrap items-center gap-1'>
                {levels?.map((yearLevel) => (
                  <Chip
                    key={`classes-${row._id}-yearLevel-${yearLevel._id}`}
                    size='small'
                    label={yearLevel.name}
                    variant='outlined'
                  />
                ))}
              </div>
            ),
            completed: completed,
            actions: (
              <div className='flex items-center gap-1 items-center justify-center'>
                {permissions['list'] && (
                  <IconTooltipButton
                    id={`preview-${row._id}`}
                    disabled={loading}
                    title='Preview'
                    icon='expand-alt'
                    onClick={() =>
                      onPreview({
                        ...row,
                        recordId: row._id,
                        questions: template?.questions || [],
                        results: results.filter((item) => item?.data?.recordId === row._id) || [],
                        survey: data?.name
                      })
                    }
                  />
                )}
                {permissions['list'] && (
                  <IconTooltipButton
                    id={`share-link-${row._id}`}
                    disabled={loading}
                    title='Share link'
                    icon='share-alt'
                    onClick={() => onShareLink(surveyId, row)}
                  />
                )}
              </div>
            )
          }
        })}
        nameModule='surveyPreviewList'
        totalRows={tempList.length ?? 0}
        skip={skip}
        rowsPerPage={rowsPerPage || 10}
        columns={[
          {
            label: '#',
            key: 'index',
            align: 'center',
            width: 10,
            show: true,
            hide: false
          },
          ...columnsTable,
          {
            label: 'Completed',
            key: 'completed',
            align: 'center',
            hide: true,
            show: true,
            delete: true,
            sort: true
          },
          {
            label: 'Actions',
            key: 'actions',
            align: 'center',
            show: true,
            hide: false
          }
        ]}
      />
      {modal && (
        <DialogPreviewAboutMe
          surveyId={surveyId}
          recordId={modal?.data?.recordId}
          type={type}
          open={modal.open}
          loading={loading}
          onClose={() => setModal(null)}
          onShareLink={(row) => onShareLink(surveyId, row)}
          data={modal.data}
        />
      )}
    </>
  )
}

export default TableAbout
